/**
 * AssessmentDesignOutcomes.jsx — Phase 2A-4
 * State machine: DRAFT → PUBLISHED → RETIRED
 * Faculty creates + publishes. Mapping to blueprint components inline per outcome.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, X, Target, Link2, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDesignShell, useIsFaculty } from "../components/assessment-design/AssessmentDesignShell";
import { LifecycleBadge } from "../components/assessment-design/LifecycleBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { "Authorization": `Bearer ${localStorage.getItem("academiaos_token")}`, "Content-Type": "application/json" }; }
function apiError(e, fb = "Error") {
  const d = e?.detail || e?.message || fb;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join("; ");
  return String(d);
}

const OUTCOME_TYPES = ["CO","PLO","CLO","ILO","SO","PEO"];
const BLOOM_LEVELS = ["REMEMBER","UNDERSTAND","APPLY","ANALYZE","EVALUATE","CREATE"];
const EMPTY_FORM = { code: "", title: "", outcome_type: "CO", description: "", bloom_level: "" };

/** Inline mapping panel shown below each PUBLISHED outcome row. */
function OutcomeMappingPanel({ outcome, isFaculty }) {
  const [mappings, setMappings] = useState([]);
  const [loadingMaps, setLoadingMaps] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showMapForm, setShowMapForm] = useState(false);
  const [mapForm, setMapForm] = useState({ component_id: "", weight_contribution_pct: "" });
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);

  const loadMappings = useCallback(async () => {
    setLoadingMaps(true);
    try {
      const r = await fetch(`${API}/assessment/outcomes/${outcome.id}/mappings`, { headers: authH() });
      if (!r.ok) throw await r.json().catch(() => ({}));
      setMappings(await r.json());
    } catch (e) { toast.error("Failed to load mappings: " + apiError(e)); }
    finally { setLoadingMaps(false); }
  }, [outcome.id]);

  const toggle = (e) => {
    // Prevent any accidental form submission from parent contexts
    if (e && e.preventDefault) e.preventDefault();
    if (!expanded) loadMappings();
    setExpanded(v => !v);
  };

  const handleMap = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { component_id: mapForm.component_id };
      if (mapForm.weight_contribution_pct) body.weight_contribution_pct = parseFloat(mapForm.weight_contribution_pct);
      const r = await fetch(`${API}/assessment/outcomes/${outcome.id}/mappings`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success("Outcome mapped to component");
      setShowMapForm(false);
      setMapForm({ component_id: "", weight_contribution_pct: "" });
      loadMappings();
    } catch (e) { toast.error("Mapping failed: " + apiError(e)); }
    finally { setSaving(false); }
  };

  const handleRemove = async (mappingId) => {
    setRemoving(mappingId);
    try {
      const r = await fetch(`${API}/assessment/mappings/${mappingId}`, {
        method: "DELETE", headers: authH(),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw d; }
      toast.success("Mapping removed");
      loadMappings();
    } catch (e) { toast.error("Remove failed: " + apiError(e)); }
    finally { setRemoving(null); }
  };

  return (
    <div className="mt-2 border-t border-slate-800/50">
      <button
        type="button"
        onClick={toggle}
        data-testid={`outcome-mappings-toggle-${outcome.id}`}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-sky-400 transition-colors py-1.5 w-full"
      >
        <Link2 className="w-3 h-3" />
        {mappings.length > 0 ? `${mappings.length} component mapping(s)` : "Component mappings"}
        {expanded ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
      </button>

      {expanded && (
        <div data-testid={`outcome-mappings-panel-${outcome.id}`} className="pb-3 space-y-2">
          {loadingMaps && <p className="text-xs text-slate-500 pl-4">Loading…</p>}
          {!loadingMaps && mappings.length === 0 && (
            <p className="text-xs text-slate-500 pl-4">No component mappings yet.</p>
          )}
          {!loadingMaps && mappings.map(m => (
            <div key={m.id} data-testid={`mapping-row-${m.id}`}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/40 text-xs text-slate-300">
              <Link2 className="w-3 h-3 text-sky-500 flex-shrink-0" />
              <span className="font-mono truncate flex-1">{m.component_id}</span>
              {m.weight_contribution_pct != null && (
                <span className="text-slate-500">{parseFloat(m.weight_contribution_pct).toFixed(1)}%</span>
              )}
              {isFaculty && (
                <button onClick={() => handleRemove(m.id)} disabled={removing === m.id}
                  data-testid={`remove-mapping-btn-${m.id}`}
                  className="p-0.5 text-slate-500 hover:text-red-400 transition-colors disabled:opacity-50">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {isFaculty && !showMapForm && (
            <button
              type="button"
              onClick={() => setShowMapForm(true)}
              data-testid={`add-mapping-btn-${outcome.id}`}
              className="flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 transition-colors px-1">
              <Plus className="w-3 h-3" /> Map to component
            </button>
          )}
          {isFaculty && showMapForm && (
            <form onSubmit={handleMap} data-testid={`mapping-form-${outcome.id}`}
              className="flex items-center gap-2 pl-1 flex-wrap">
              <input
                data-testid={`mapping-component-id-input-${outcome.id}`}
                value={mapForm.component_id}
                onChange={e => setMapForm(f => ({ ...f, component_id: e.target.value }))}
                required placeholder="Component UUID"
                className="flex-1 min-w-0 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-200 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-sky-500 placeholder:text-slate-500"
              />
              <input
                data-testid={`mapping-weight-input-${outcome.id}`}
                type="number" step="0.01" min="0" max="100"
                value={mapForm.weight_contribution_pct}
                onChange={e => setMapForm(f => ({ ...f, weight_contribution_pct: e.target.value }))}
                placeholder="Wt% (optional)"
                className="w-24 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-200 text-xs focus:outline-none focus:ring-1 focus:ring-sky-500 placeholder:text-slate-500"
              />
              <button type="submit" disabled={saving}
                data-testid={`mapping-submit-btn-${outcome.id}`}
                className="px-2.5 py-1 rounded text-xs bg-sky-700 hover:bg-sky-600 text-white disabled:opacity-50 transition-colors">
                {saving ? "…" : "Map"}
              </button>
              <button type="button" onClick={() => setShowMapForm(false)}
                className="p-1 text-slate-500 hover:text-slate-300">
                <X className="w-3 h-3" />
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export default function AssessmentDesignOutcomes() {
  const isFaculty = useIsFaculty();
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [actioning, setActioning] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filterStatus ? `?status=${filterStatus}` : "";
      const r = await fetch(`${API}/assessment/outcomes${qs}`, { headers: authH() });
      if (!r.ok) throw await r.json().catch(() => ({}));
      setOutcomes(await r.json());
    } catch (e) { toast.error("Failed to load outcomes: " + apiError(e)); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { code: form.code, title: form.title, outcome_type: form.outcome_type };
      if (form.description) body.description = form.description;
      if (form.bloom_level) body.bloom_level = form.bloom_level;
      const r = await fetch(`${API}/assessment/outcomes`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Outcome "${data.title}" created`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error("Create failed: " + apiError(e)); }
    finally { setSaving(false); }
  };

  const handleAction = async (outcome, action) => {
    setActioning(`${outcome.id}-${action}`);
    try {
      const r = await fetch(`${API}/assessment/outcomes/${outcome.id}/${action}`, {
        method: "POST", headers: authH(),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Outcome ${action}d`);
      load();
    } catch (e) { toast.error(`${action} failed: ` + apiError(e)); }
    finally { setActioning(null); }
  };

  return (
    <AssessmentDesignShell title="Learning Outcomes" breadcrumb="Outcomes">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-400">
          Learning outcomes map to blueprint components.{" "}
          <span className="font-mono text-slate-300">DRAFT → PUBLISHED → RETIRED</span>
        </p>
        <div className="flex items-center gap-2">
          <select data-testid="outcomes-status-filter"
            value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
            <option value="">All Statuses</option>
            <option value="DRAFT">DRAFT</option>
            <option value="PUBLISHED">PUBLISHED</option>
            <option value="RETIRED">RETIRED</option>
          </select>
          <button onClick={load} disabled={loading} data-testid="outcomes-refresh-btn"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {isFaculty && (
            <button onClick={() => setShowCreate(true)} data-testid="outcome-create-button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors">
              <Plus className="w-4 h-4" /> New Outcome
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-slate-800/40 animate-pulse" />)}</div>
      ) : outcomes.length === 0 ? (
        <div data-testid="outcomes-empty" className="text-center py-16 text-slate-500 text-sm">
          No learning outcomes{filterStatus ? ` with status "${filterStatus}"` : ""}.
          {isFaculty && !filterStatus && (
            <button onClick={() => setShowCreate(true)} className="block mt-2 text-sky-400 hover:text-sky-300 mx-auto">
              Create first outcome →
            </button>
          )}
        </div>
      ) : (
        <div data-testid="outcomes-list" className="space-y-2">
          {outcomes.map(oc => (
            <div key={oc.id} data-testid={`outcome-row-${oc.id}`}
              className="p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:border-slate-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-slate-100 truncate">{oc.title}</span>
                    <LifecycleBadge status={oc.status} />
                    <span className="text-xs px-1.5 py-0.5 rounded border border-slate-700 text-slate-400">
                      {oc.outcome_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="font-mono">{oc.code}</span>
                    {oc.bloom_level && <span>Bloom: {oc.bloom_level}</span>}
                  </div>
                  {oc.description && (
                    <p className="text-xs text-slate-400 mt-1 truncate">{oc.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isFaculty && oc.status === "DRAFT" && (
                    <button onClick={() => handleAction(oc, "publish")}
                      disabled={actioning === `${oc.id}-publish`}
                      data-testid={`outcome-publish-btn-${oc.id}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-300 border border-emerald-700/50 disabled:opacity-50 transition-colors">
                      {actioning === `${oc.id}-publish` ? "…" : "Publish"}
                    </button>
                  )}
                  {oc.status === "PUBLISHED" && (
                    <span className="text-xs text-slate-500">Published</span>
                  )}
                </div>
              </div>

              {/* Inline mapping panel for published outcomes */}
              {oc.status === "PUBLISHED" && (
                <OutcomeMappingPanel outcome={oc} isFaculty={isFaculty} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div data-testid="outcome-create-modal" className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-base font-semibold text-slate-100">Create Learning Outcome</h2>
              <button onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Code *</label>
                  <input data-testid="outcome-code-input" value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value }))} required
                    placeholder="e.g. CO-1"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Type *</label>
                  <select data-testid="outcome-type-select" value={form.outcome_type}
                    onChange={e => setForm(f => ({ ...f, outcome_type: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                    {OUTCOME_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Title *</label>
                <input data-testid="outcome-title-input" value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
                  placeholder="Outcome statement"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Bloom Level</label>
                <select data-testid="outcome-bloom-select" value={form.bloom_level}
                  onChange={e => setForm(f => ({ ...f, bloom_level: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                  <option value="">None</option>
                  {BLOOM_LEVELS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Description</label>
                <textarea data-testid="outcome-description-input" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} placeholder="Optional description"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500 resize-none" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
                  className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving} data-testid="outcome-create-submit-btn"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                  {saving ? "Creating…" : "Create Outcome"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AssessmentDesignShell>
  );
}
